import { useCallback, useEffect, useState } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import "./TrendsPage.css";
import { getSalaryComparisonTwoTerritories } from "./api";
import { parseNum, fmt, fmtPct, payChangeClass, parseReviewPeriod, FORECAST } from "./trendsFormat";
import TrendsMixedChart from "./TrendsMixedChart";
import TrendsSearchSelect from "./TrendsSearchSelect";
import SalaryBreakdownDialog from "./SalaryBreakdownDialog";

/**
 * TrendsTwoLocations - "Role × 2 Locations" tab.
 *
 * React port of role-x-2-locations.component.ts: one role across two
 * territories, each with its own inflation series, plus the COST GAP
 * (A − B) per period. Defaults preserved: Country A = first territory,
 * Country B = LAST territory (opposite ends of the list). A === B stays a
 * valid selection - "a country compared to itself is a legitimate zero-gap
 * result" (deliberate fix in the old code, do not re-block it).
 * KPI cards: cost gap first period / cost gap latest period / gap movement
 * ("Business case strengthening/weakening").
 */
export default function TrendsTwoLocations({ startYear, endYear, startMonth, jobTitles, territories }) {
  const [jobCode, setJobCode] = useState(null);
  const [territoryIdA, setTerritoryIdA] = useState(null);
  const [territoryIdB, setTerritoryIdB] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [breakdown, setBreakdown] = useState(null);

  // Defaults: A = first, B = last territory (verbatim from old).
  useEffect(() => {
    if (territories.length > 0) {
      if (territoryIdA == null) setTerritoryIdA(territories[0].id);
      if (territoryIdB == null) setTerritoryIdB(territories[territories.length - 1].id);
    }
  }, [territories, territoryIdA, territoryIdB]);

  const canLoad = jobCode && territoryIdA && territoryIdB && startYear && endYear;
  const load = useCallback(() => {
    if (!canLoad) return;
    setLoading(true);
    getSalaryComparisonTwoTerritories({ startYear, endYear, startMonth, territoryIdA, territoryIdB, minervaJobCode: jobCode })
      .then((data) => {
        setRows(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => {
        setRows([]);
        setLoading(false);
      });
  }, [canLoad, startYear, endYear, startMonth, territoryIdA, territoryIdB, jobCode]);

  useEffect(() => {
    load();
  }, [load]);

  const hasData = rows.length > 0;
  const hasForecast = rows.some((r) => r.is_forecasted_data === true);

  const countryName = (id) => territories.find((t) => t.id === id)?.name || "";
  const nameA = countryName(territoryIdA);
  const nameB = countryName(territoryIdB);
  const roleName = jobTitles.find((j) => j.job_code === jobCode)?.name || jobCode || "";

  // computeKpis() - verbatim port.
  const first = rows[0];
  const latest = rows[rows.length - 1];
  const costGapFirst = first ? parseNum(first.cost_gap) : null;
  const costGapLatest = latest ? parseNum(latest.cost_gap) : null;
  const gapMovement =
    costGapFirst !== null && costGapLatest !== null ? costGapLatest - costGapFirst : null;
  const gapMovementLabel =
    gapMovement === null ? "" : gapMovement > 0 ? "Business case strengthening" : "Business case weakening";

  function openBreakdown(row, which) {
    const tid = which === "A" ? territoryIdA : territoryIdB;
    const tname = which === "A" ? nameA : nameB;
    const { startMon, endMon, fullStartYear, fullEndYear } = parseReviewPeriod(row, startYear, endYear);
    setBreakdown({
      minervaJobCode: jobCode,
      territoryId: tid,
      startMonth: startMon,
      startYear: fullStartYear,
      endMonth: endMon,
      endYear: fullEndYear,
      label: `${roleName} — ${tname} (${row.review_period})`,
    });
  }

  async function exportExcel() {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Role × 2 Locations");
    // Merged two-row header: A1:A2 Period | B1:E1 country A | F1:I1 country B |
    // J1:J2 cost gap | K1:K2 forecast.
    ws.mergeCells("A1:A2"); ws.getCell("A1").value = "Period";
    ws.mergeCells("B1:E1"); ws.getCell("B1").value = nameA;
    ws.mergeCells("F1:I1"); ws.getCell("F1").value = nameB;
    ws.mergeCells("J1:J2"); ws.getCell("J1").value = "Cost Gap";
    ws.mergeCells("K1:K2"); ws.getCell("K1").value = "Forecast";
    const SUBS = ["Salary (£)", "Wage Δ", "Inflation", "Real Δ"];
    ["B2", "C2", "D2", "E2"].forEach((c, i) => { ws.getCell(c).value = SUBS[i]; });
    ["F2", "G2", "H2", "I2"].forEach((c, i) => { ws.getCell(c).value = SUBS[i]; });

    const NAVY = "FF1F4E78", BLUE = "FF2C6CB7", CYAN = "FF66C3D0";
    ["A1", "K1"].forEach((c) => {
      ws.getCell(c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: NAVY } };
      ws.getCell(c).font = { bold: true, color: { argb: "FFFFFFFF" } };
    });
    ["B1", "B2", "C2", "D2", "E2"].forEach((c) => {
      ws.getCell(c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: BLUE } };
      ws.getCell(c).font = { bold: true, color: { argb: "FFFFFFFF" } };
    });
    ["F1", "F2", "G2", "H2", "I2"].forEach((c) => {
      ws.getCell(c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: CYAN } };
      ws.getCell(c).font = { bold: true, color: { argb: "FFFFFFFF" } };
    });
    ws.getCell("J1").fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFDBEAFE" } };
    ws.getCell("J1").font = { bold: true, color: { argb: "FF1E40AF" } };
    [1, 2].forEach((rn) => {
      ws.getRow(rn).eachCell((c) => {
        c.alignment = { horizontal: "center", vertical: "middle" };
      });
    });
    ws.columns = [
      { width: 16 },
      { width: 13 }, { width: 11 }, { width: 11 }, { width: 11 },
      { width: 13 }, { width: 11 }, { width: 11 }, { width: 11 },
      { width: 13 }, { width: 10 },
    ];

    const AMBER = "FFFFF3CD";
    rows.forEach((r) => {
      const row = ws.addRow([
        r.review_period,
        parseNum(r.salary_a), parseNum(r.pay_change_a), parseNum(r.inflation_a), parseNum(r.real_change_a),
        parseNum(r.salary_b), parseNum(r.pay_change_b), parseNum(r.inflation_b), parseNum(r.real_change_b),
        parseNum(r.cost_gap),
        r.is_forecasted_data ? "Yes" : "No",
      ]);
      [2, 6].forEach((ci) => { row.getCell(ci).numFmt = '£#,##0.00'; });
      [3, 4, 5, 7, 8, 9].forEach((ci) => { row.getCell(ci).numFmt = '0.0"%"'; });
      const gapCell = row.getCell(10);
      gapCell.numFmt = "£#,##0";
      gapCell.font = { bold: true, color: { argb: "FF1E40AF" } };
      gapCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFDBEAFE" } };
      [3, 5, 7, 9].forEach((ci) => {
        const v = row.getCell(ci).value;
        if (typeof v === "number" && v !== 0) {
          row.getCell(ci).font = { color: { argb: v > 0 ? "FF107C41" : "FFC0392B" }, bold: true };
        }
      });
      if (r.is_forecasted_data) {
        row.eachCell((c) => {
          c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: AMBER } };
        });
      }
    });
    ws.eachRow((row) => {
      row.eachCell((c) => {
        c.border = {
          top: { style: "thin" }, bottom: { style: "thin" },
          left: { style: "thin" }, right: { style: "thin" },
        };
      });
    });
    const buffer = await wb.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], { type: "application/octet-stream" }),
      `Role-x-2-Locations_${roleName}_${nameA}_vs_${nameB}.xlsx`.replace(/\s+/g, "-")
    );
  }

  return (
    <div className="tr-tab">
      <div className="tr-mode-eyebrow">One role &times; two countries vs inflation</div>
      <div className="tr-filters">
        <label className="tr-field tr-field-role">
          <span className="tr-field-label">Role<span className="tr-req">*</span></span>
          <TrendsSearchSelect
            options={jobTitles.map((j) => ({ value: j.job_code, label: j.name, sub: j.sub_stack }))}
            value={jobCode}
            placeholder="Select role"
            onChange={setJobCode}
          />
        </label>
        <label className="tr-field">
          <span className="tr-field-label">Country A<span className="tr-req">*</span></span>
          <select className="tr-select" value={territoryIdA ?? ""} onChange={(e) => setTerritoryIdA(Number(e.target.value))}>
            {territories.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </label>
        <label className="tr-field">
          <span className="tr-field-label">Country B<span className="tr-req">*</span></span>
          <select className="tr-select" value={territoryIdB ?? ""} onChange={(e) => setTerritoryIdB(Number(e.target.value))}>
            {territories.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </label>
        {hasData && (
          <button type="button" className="tr-export-btn" onClick={exportExcel}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 3v18" /></svg>
            Export to Excel
          </button>
        )}
      </div>

      {!hasData && !loading && (
        <div className="tr-empty">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 17l6-6 4 4 8-8" /><path d="M15 7h6v6" /></svg>
          <p>Select a role and two countries above to view analysis.</p>
        </div>
      )}
      {loading && <p className="tr-status">Loading…</p>}

      {hasData && !loading && (
        <>
          <div className="tr-kpis tr-kpis-3">
            <div className="tr-kpi">
              <div className="tr-kpi-label">COST GAP {first?.review_period}</div>
              <div className="tr-kpi-value">{fmt(costGapFirst)}</div>
              <div className="tr-kpi-sub">{nameA} − {nameB}</div>
            </div>
            <div className="tr-kpi">
              <div className="tr-kpi-label">COST GAP {latest?.review_period}</div>
              <div className="tr-kpi-value">{fmt(costGapLatest)}</div>
              <div className="tr-kpi-sub">Saving per FTE today</div>
            </div>
            <div className="tr-kpi">
              <div className="tr-kpi-label">GAP MOVEMENT</div>
              <div className={`tr-kpi-value ${payChangeClass(gapMovement)}`}>{fmt(gapMovement)}</div>
              <div className="tr-kpi-sub">{gapMovementLabel}</div>
            </div>
          </div>

          {hasForecast && (
            <div className="tr-forecast-banner">
              Window includes forecast data. Amber-shaded rows &amp; regions are projected.
              Sources: BoE / ECB / Fed / IMF / RBI / SARB / BCB.
            </div>
          )}

          <TrendsMixedChart
            labels={rows.map((r) => r.review_period)}
            forecastFlags={rows.map((r) => !!r.is_forecasted_data)}
            bars={[
              {
                label: `${nameA} salary`,
                fill: "rgba(44,108,183,0.75)",
                border: "#2C6CB7",
                forecastFill: FORECAST.barFillStrong,
                values: rows.map((r) => parseNum(r.salary_a)),
              },
              {
                label: `${nameB} salary`,
                fill: "rgba(102,195,208,0.65)",
                border: "#66c3d0",
                forecastFill: FORECAST.barFillSoft,
                values: rows.map((r) => parseNum(r.salary_b)),
              },
            ]}
            lines={[
              // Pay-change lines plot null as 0 (old-chart behavior);
              // tooltips read the raw nulls as an em dash.
              {
                label: `${nameA} pay change`, color: "#1a3d6b", dash: null,
                values: rows.map((r) => parseNum(r.pay_change_a) ?? 0),
                raw: rows.map((r) => parseNum(r.pay_change_a)),
              },
              {
                label: `${nameB} pay change`, color: "#66c3d0", dash: [5, 4],
                values: rows.map((r) => parseNum(r.pay_change_b) ?? 0),
                raw: rows.map((r) => parseNum(r.pay_change_b)),
              },
              { label: `${nameA} inflation`, color: "#E4572E", dash: [2, 2], values: rows.map((r) => parseNum(r.inflation_a)) },
              { label: `${nameB} inflation`, color: "#F2A65A", dash: [2, 2], values: rows.map((r) => parseNum(r.inflation_b)) },
            ]}
            height={340}
          />

          <div className="tr-table-wrap">
            <div className="tr-table-title">Period breakdown</div>
            <table className="tr-table tr-table-grouped">
              <thead>
                <tr>
                  <th rowSpan="2">PERIOD</th>
                  <th colSpan="4" className="tr-group-a">{nameA.toUpperCase()}</th>
                  <th colSpan="4" className="tr-group-b">{nameB.toUpperCase()}</th>
                  <th rowSpan="2" className="tr-group-gap">COST GAP</th>
                </tr>
                <tr>
                  <th className="tr-group-a">SALARY</th>
                  <th className="tr-group-a">WAGE Δ</th>
                  <th className="tr-group-a">INFLATION</th>
                  <th className="tr-group-a">REAL Δ</th>
                  <th className="tr-group-b">SALARY</th>
                  <th className="tr-group-b">WAGE Δ</th>
                  <th className="tr-group-b">INFLATION</th>
                  <th className="tr-group-b">REAL Δ</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className={r.is_forecasted_data ? "tr-forecast-row" : ""}>
                    <td>
                      {r.review_period}
                      {r.is_forecasted_data && <span className="tr-f-badge" title="Forecast">F</span>}
                    </td>
                    <td className="tr-band-a">
                      {fmt(r.salary_a)}
                      <button type="button" className="tr-info-btn" aria-label={`${nameA} salary breakdown`} onClick={() => openBreakdown(r, "A")}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></svg>
                      </button>
                    </td>
                    <td className={`tr-band-a ${payChangeClass(r.pay_change_a)}`}>{fmtPct(r.pay_change_a)}</td>
                    <td className="tr-band-a">{parseNum(r.inflation_a) === null ? "—" : `${parseNum(r.inflation_a)}%`}</td>
                    <td className={`tr-band-a ${payChangeClass(r.real_change_a)}`}>{fmtPct(r.real_change_a)}</td>
                    <td className="tr-band-b">
                      {fmt(r.salary_b)}
                      <button type="button" className="tr-info-btn" aria-label={`${nameB} salary breakdown`} onClick={() => openBreakdown(r, "B")}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></svg>
                      </button>
                    </td>
                    <td className={`tr-band-b ${payChangeClass(r.pay_change_b)}`}>{fmtPct(r.pay_change_b)}</td>
                    <td className="tr-band-b">{parseNum(r.inflation_b) === null ? "—" : `${parseNum(r.inflation_b)}%`}</td>
                    <td className={`tr-band-b ${payChangeClass(r.real_change_b)}`}>{fmtPct(r.real_change_b)}</td>
                    <td className="tr-cost-gap">{fmt(r.cost_gap)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <SalaryBreakdownDialog open={!!breakdown} request={breakdown} onClose={() => setBreakdown(null)} />
    </div>
  );
}
